import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useSession } from '@/lib/auth';
import { ApiError, api } from '@/lib/api';

export const Route = createFileRoute('/_authenticated/tools')({
  component: ToolsPage,
});

function ToolsPage() {
  const { user } = useSession();

  if (!user || user.role.slug !== 'super_admin') {
    return (
      <div className="p-4 sm:p-8">
        <div className="mx-auto max-w-3xl">
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900">
            หน้านี้สำหรับ <span className="font-mono">super_admin</span> เท่านั้น
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8">
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="flex items-center gap-2">
          <Wrench className="h-6 w-6 text-slate-400" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
              Tools
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              งาน maintenance ที่ต้องใช้สิทธิ์ super_admin
            </p>
          </div>
        </div>

        <ArchiveStaleDraftsCard />
      </div>
    </div>
  );
}

/**
 * Archives drafts older than a configurable threshold (default 60
 * days, min 7). Supports a dry-run preview so the operator can size
 * the impact before committing.
 */
function ArchiveStaleDraftsCard() {
  const [days, setDays] = useState(60);
  const [state, setState] = useState<{
    status: 'idle' | 'running' | 'done' | 'error';
    mode: 'dry' | 'apply' | null;
    result: { count: number; preview?: { id: string; slug: string }[] } | null;
    error: string | null;
  }>({ status: 'idle', mode: null, result: null, error: null });

  const run = async (dry: boolean) => {
    setState({ status: 'running', mode: dry ? 'dry' : 'apply', result: null, error: null });
    try {
      const params = new URLSearchParams({ days: String(days) });
      if (dry) params.set('dry', 'true');
      const res = await api<{
        dryRun: boolean;
        days: number;
        candidateCount?: number;
        archivedCount?: number;
        candidatesPreview?: { id: string; slug: string }[];
      }>(`/api/admin/_archive-stale-drafts?${params.toString()}`, { method: 'POST' });
      setState({
        status: 'done',
        mode: dry ? 'dry' : 'apply',
        result: {
          count: dry ? (res.candidateCount ?? 0) : (res.archivedCount ?? 0),
          preview: res.candidatesPreview,
        },
        error: null,
      });
    } catch (e) {
      setState((s) => ({
        ...s,
        status: 'error',
        error: e instanceof ApiError ? `${e.code}: ${e.message}` : String(e),
      }));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Archive stale drafts</CardTitle>
        <CardDescription>
          เลื่อนสถานะ draft ที่ไม่ถูกแก้นานเกิน threshold (วัน) ไปเป็น <code>archived</code> —
          เก็บใน DB กู้คืนได้ผ่าน status edit. กด "ตรวจสอบ" ก่อนเพื่อ preview จำนวน
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500" htmlFor="archive-days">
            Threshold (วัน)
          </label>
          <input
            id="archive-days"
            type="number"
            min={7}
            value={days}
            onChange={(e) => setDays(Math.max(7, parseInt(e.target.value, 10) || 60))}
            className="h-8 w-20 rounded-md border border-slate-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          />
          <span className="text-[11px] text-slate-400">ขั้นต่ำ 7 วัน</span>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => void run(true)}
            disabled={state.status === 'running'}
          >
            {state.status === 'running' && state.mode === 'dry'
              ? 'กำลังตรวจ...'
              : 'ตรวจสอบ (dry run)'}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => void run(false)}
            disabled={state.status === 'running'}
          >
            {state.status === 'running' && state.mode === 'apply'
              ? 'กำลัง archive...'
              : 'Archive จริง'}
          </Button>
        </div>
        {state.status === 'error' && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 font-mono text-xs text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300">
            {state.error}
          </div>
        )}
        {state.status === 'done' && state.result && (
          <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs dark:border-slate-700 dark:bg-slate-800/50">
            <div>
              <span
                className={
                  state.mode === 'dry'
                    ? 'rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-700'
                    : 'rounded bg-emerald-100 px-1.5 py-0.5 font-medium text-emerald-700'
                }
              >
                {state.mode === 'dry' ? 'Dry run' : 'Applied'}
              </span>
              <span className="ml-2 text-slate-600 dark:text-slate-300">
                {state.mode === 'dry'
                  ? `จะ archive ${state.result.count} draft`
                  : `archived ${state.result.count} draft`}
              </span>
            </div>
            {state.result.preview && state.result.preview.length > 0 && (
              <details className="text-[11px]">
                <summary className="cursor-pointer text-slate-500">
                  ดู preview ({state.result.preview.length} รายการแรก)
                </summary>
                <ul className="mt-1 max-h-40 overflow-y-auto font-mono">
                  {state.result.preview.map((p) => (
                    <li key={p.id} className="text-slate-500">
                      {p.id} · /{p.slug}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
